import { useEffect, useState, useMemo } from "react";
import { Heart, ExternalLink, PawPrint, Stethoscope, Cookie, Home as HomeIcon } from "lucide-react";
import { api } from "@/lib/api";
import DogImage from "@/components/DogImage";
import PageMeta from "@/components/PageMeta";
import { ZEFFY_MAIN } from "@/constants/config";
import { logError } from "@/lib/log";

const TIERS = [
  { amount: 10, label: "Weekly Food", icon: Cookie, note: "One week of quality food + treats" },
  { amount: 25, label: "Vaccines", icon: Stethoscope, note: "Core vaccinations & preventative care" },
  { amount: 50, label: "Vet Visit", icon: PawPrint, note: "One check-up, deworm & basic labs" },
  { amount: 100, label: "Surgery Fund", icon: Heart, note: "Contributes toward a life-saving procedure" },
];

const CARE_FUND = {
  id: "care-fund",
  name: "Rescue Care Fund",
  isFund: true,
  headline: "Help every paw",
  story:
    "Not sure which pup to sponsor? The Rescue Care Fund goes wherever the need is greatest — medical emergencies, transport from Hawaiʻi, food, boarding, and getting the next dog rescued. Your monthly gift keeps our doors open for every paw that needs us.",
  covers: [
    "Medical treatments the day they walk in",
    "Transport from Hawaiʻi to the West Coast",
    "Boarding, food and daily care",
    "Emergency vet bills",
  ],
};

export default function Sponsor() {
  const [dogs, setDogs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tier, setTier] = useState(25);

  useEffect(() => {
    api.get("/dogs")
      .then((r) => setDogs((r.data || []).filter((d) => ["available", "pending"].includes(d.status))))
      .catch((err) => logError("[Sponsor] failed to load dogs:", err));
  }, []);

  useEffect(() => {
    if (!selected) setSelected(CARE_FUND);
  }, [selected]);

  const sponsorables = useMemo(() => [CARE_FUND, ...dogs], [dogs]);
  const activeAmount = tier;
  const zeffyTarget = selected?.zeffy_url || ZEFFY_MAIN;
  const activeName = selected?.name || CARE_FUND.name;

  return (
    <div>
      <PageMeta
        title="Sponsor a Bulldog Monthly — Kōkua a Pup | Lavish 808 Bulliez"
        description="Sponsor a rescued bulldog every month. Choose a specific dog or the Rescue Care Fund. From $10/month, your kōkua funds food, vaccines, vet visits and life-saving surgeries."
      />

      {/* Hero */}
      <section className="pt-20 pb-12" style={{ backgroundColor: "#F7ECD9" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.35em] mb-3" style={{ color: "#E27E4E" }}>
            Kōkua • Monthly Sponsorship
          </div>
          <h1 className="font-serif text-4xl md:text-6xl leading-tight mb-4" style={{ color: "#0E5C6E" }}>
            Sponsor a pup.<br />Change a life every month.
          </h1>
          <p className="text-lg max-w-2xl leading-relaxed" style={{ color: "#2D2B2A" }}>
            Choose a specific dog or the Rescue Care Fund. Pick an amount. Your monthly kōkua funds
            food, vaccines, vet care, transport from Hawaiʻi, and the second chance every paw deserves.
          </p>
        </div>
      </section>

      {/* Step 1: Choose who */}
      <section className="py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader step="1" title="Choose who to help" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sponsorables.map((item) => (
              <SponsorCard
                key={item.id}
                item={item}
                selected={selected?.id === item.id}
                onSelect={() => setSelected(item)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Step 2: Amount */}
      <section className="py-14" style={{ backgroundColor: "#FDF9F1" }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader step="2" title="Choose your monthly amount" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {TIERS.map((t) => (
              <TierCard key={t.amount} t={t} active={tier === t.amount} onClick={() => setTier(t.amount)} />
            ))}
          </div>
          <div className="mt-8 text-center text-sm" style={{ color: "#6B655E" }}>
            Prefer a custom amount? You'll set that on the next screen at Zeffy.
          </div>
        </div>
      </section>

      {/* Step 3: Commit */}
      <section className="py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionHeader step="3" title={`Sponsor ${activeName} for $${activeAmount}/month`} />
          <p className="mb-8" style={{ color: "#2D2B2A" }}>
            Continue to Zeffy to set up your recurring monthly gift. <strong>0% platform fees</strong> — 100% of your kōkua reaches the pups.
          </p>
          <a
            href={zeffyTarget}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-donate inline-flex"
            data-testid="sponsor-continue"
          >
            <Heart size={18} fill="white" /> Continue to Zeffy <ExternalLink size={14} />
          </a>
          <div className="text-xs mt-4" style={{ color: "#6B655E" }}>
            You can pause, adjust or cancel your sponsorship anytime through your Zeffy account.
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16" style={{ backgroundColor: "#F7ECD9" }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl md:text-4xl text-center mb-10" style={{ color: "#0E5C6E" }}>
            Questions ʻohana ask
          </h2>
          <div className="space-y-6">
            <Faq q="What happens when my sponsored dog is adopted?" a="You'll get a celebration email letting you know they found their forever ʻohana. You'll choose whether to redirect your monthly gift to the Rescue Care Fund, move it to another pup, or pause. If we don't hear back within 14 days, we'll gently move it to the Rescue Care Fund so no gift goes unused — and we'll email you a friendly confirmation you can reply to anytime." />
            <Faq q="How much of my sponsorship goes to the dogs?" a="100%. Zeffy charges zero platform fees to nonprofits, so every dollar reaches food, vet care, transport, and rehabilitation. We show you what your tier covers up front for full transparency." />
            <Faq q="Can I sponsor more than one dog?" a="ʻAe! Just set up separate monthly gifts — one per dog. Some of our ʻohana sponsor a pup for each family member." />
            <Faq q="Can I cancel or adjust anytime?" a="Yes, always. Log into your Zeffy account, or email us at info@lavish808buliezrescuedpaws.org and we'll take care of it same-day." />
            <Faq q="Is my sponsorship tax-deductible?" a="Yes. We are a registered 501(c)(3) (EIN 42-3174937). Zeffy sends you an official receipt every year for your records." />
          </div>
        </div>
      </section>
    </div>
  );
}

function SectionHeader({ step, title }) {
  return (
    <div className="text-center mb-10">
      <div className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: "#E27E4E" }}>
        Step {step}
      </div>
      <h2 className="font-serif text-3xl md:text-4xl" style={{ color: "#0E5C6E" }}>{title}</h2>
    </div>
  );
}

function SponsorCard({ item, selected, onSelect }) {
  const isFund = item.isFund;
  return (
    <button
      type="button"
      onClick={onSelect}
      className="text-left rounded-3xl overflow-hidden transition-all"
      style={{
        border: selected ? "3px solid #E27E4E" : "3px solid transparent",
        boxShadow: selected ? "0 10px 30px rgba(226,126,78,0.25)" : "0 4px 12px rgba(0,0,0,0.05)",
      }}
      data-testid={`sponsor-card-${isFund ? "care-fund" : item.name.toLowerCase().replace(/[^a-z]/g, "")}`}
    >
      <div className="aspect-[4/3] overflow-hidden" style={{ backgroundColor: "#0E5C6E" }}>
        {isFund ? (
          <div className="w-full h-full flex flex-col items-center justify-center p-8 text-center" style={{ backgroundColor: "#0E5C6E", color: "white" }}>
            <HomeIcon size={44} className="mb-4" />
            <div className="font-serif text-2xl">Rescue Care Fund</div>
            <div className="text-xs uppercase tracking-[0.25em] text-white/70 mt-2">Help every paw</div>
          </div>
        ) : (
          <DogImage dog={item} />
        )}
      </div>
      <div className="p-6 bg-white">
        {!isFund && (
          <>
            <div className="font-serif text-2xl mb-1" style={{ color: "#0E5C6E" }}>{item.name}</div>
            <div className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: item.status === "pending" ? "#B48A2E" : "#E27E4E" }}>
              {item.status === "pending" ? "Pending adoption" : "Available"}
            </div>
          </>
        )}
        <p className="text-sm leading-relaxed mb-4" style={{ color: "#2D2B2A" }}>
          {isFund ? item.story : (item.story || "").slice(0, 140) + "..."}
        </p>
        <div className="text-xs uppercase tracking-[0.2em] mb-2" style={{ color: "#E27E4E" }}>
          Sponsorship covers
        </div>
        <ul className="text-xs space-y-1" style={{ color: "#6B655E" }}>
          {(isFund ? item.covers : ["Food & treats", "Medical care", "Rehabilitation", "Foster support"]).map((c) => (
            <li key={c} className="flex gap-2">
              <span style={{ color: "#E27E4E" }}>•</span> <span>{c}</span>
            </li>
          ))}
        </ul>
        {selected && (
          <div className="mt-4 text-xs font-medium inline-flex items-center gap-1" style={{ color: "#E27E4E" }}>
            <Heart size={12} fill="#E27E4E" /> Selected
          </div>
        )}
      </div>
    </button>
  );
}

function TierCard({ t, active, onClick }) {
  const Icon = t.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-2xl p-6 text-center transition-all"
      style={{
        backgroundColor: active ? "#0E5C6E" : "white",
        color: active ? "white" : "#2D2B2A",
        border: active ? "3px solid #E27E4E" : "3px solid transparent",
        boxShadow: active ? "0 10px 30px rgba(14,92,110,0.25)" : "0 4px 12px rgba(0,0,0,0.05)",
      }}
      data-testid={`sponsor-tier-${t.amount}`}
    >
      <Icon size={28} className="mx-auto mb-3" style={{ color: active ? "white" : "#E27E4E" }} />
      <div className="font-serif text-4xl mb-1">${t.amount}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] mb-2 opacity-80">/ month</div>
      <div className="text-xs font-medium mb-1">{t.label}</div>
      <div className="text-xs opacity-75">{t.note}</div>
    </button>
  );
}

function Faq({ q, a }) {
  return (
    <details className="bg-white rounded-2xl p-6 group" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
      <summary className="cursor-pointer font-serif text-lg font-medium list-none flex justify-between items-center" style={{ color: "#0E5C6E" }}>
        {q}
        <span className="text-2xl group-open:rotate-45 transition-transform" style={{ color: "#E27E4E" }}>+</span>
      </summary>
      <p className="mt-4 leading-relaxed" style={{ color: "#2D2B2A" }}>{a}</p>
    </details>
  );
}
